import React, {useEffect, useState} from 'react';
import BTable from 'react-bootstrap/Table';
import {Cell, Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable} from 'react-table';
import {FormattedMessage, useIntl} from 'react-intl';
import {isEmpty} from 'lodash';
import {api} from '../../api';
import ReactTablePagination from './ReactTablePagination';
import PageLoadingIndicator from '../Layout/PageLoadingIndicator';
import DaysUntilColumn from './Table/Columns/DaysUntilColumn';
import ActionColumn from './Table/Columns/ActionColumn';
import ReviewsColumn from './Table/Columns/ReviewsColumn';
import {loadUserProfile} from '../../auth';
import {KeycloakProfile} from '../../vendor/keycloak-js/keycloak';
import {ListingCriteriaScore, LocalizedText} from '../../types/types';

export const URL_SLUG = 'reviews';

interface PaperSubmissionReviewListingData {
    id: string;
    submission: {
        id: string;
        title: string;
        topic: LocalizedText;
        callForPapersUrlSlug: string;
    };
    reviewer: {
        id: string;
        keycloakId: string;
        email: string;
    };
    event: {
        id: string;
        name: string;
        startDate: string;
    };
    reviewableUntil: string;
    criteriaScores: ListingCriteriaScore[];
    reasoning?: string;
}

/**
 * Spalten-Filter für Veranstaltungsnamen
 *
 * @constructor
 * @param placeholder
 */
    // @ts-ignore FIXME Typehinting
const ColumnFilter = (placeholder: string) => ({column: {filterValue, setFilter}}) => {
    return (
        <input
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder={placeholder}
        />
    );
};

/**
 * Tabellarisches Listing der zu bewertenden Einreichungen des aktuell eingeloggten Users
 *
 * @constructor
 */
const PaperSubmissionReviewListing: React.FC = () => {
    const intl = useIntl();
    const [keycloakUser, setKeycloakUser] = useState<KeycloakProfile | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<PaperSubmissionReviewListingData[]>([]);
    const [filteredData, setFilteredData] = useState<PaperSubmissionReviewListingData[]>([]);
    const [showFilter, setShowFilter] = useState(false);
    const [showNotReviewed, setShowNotReviewed] = useState(false);

    useEffect(() => {
        const loadSubmissionForUser = async () => {
            const response = await api.fetchAllSubmissionReviewsForUser();
            setIsLoading(false);

            await loadUserProfile().then((userProfile: KeycloakProfile) => {
                setKeycloakUser(userProfile);
            });

            return response;
        };

        loadSubmissionForUser().then(res => {
            if (res !== undefined) {
                setData(res.results);
                // Gefiltere Daten initial setzen und data als "immutable" behalten
                setFilteredData(res.results);
            }
        });
    }, []);

    /**
     * Globale Filterfunktion für die Tabelle.
     * Zur Zeit ein Filter ob für eine Einreichung Bewertungen durchgeführt wurden.
     *
     * @constructor
     */
    const GlobalFilter = () => {
        const filterNotReviewedSubmissions = () => {
            // Umgekehrte Logik, da state-update asynchron ist und diese Abfrage somit lieber davor getätigt wird
            // anstatt das ganze asynchron zu machen und await zu verwenden
            if (!showNotReviewed) {
                setFilteredData(data.filter((review: PaperSubmissionReviewListingData) => {
                    // Wenn die Bewertung keine Scorings hat und keine Ablehnbegründung, gilt die Bewertung als noch ausstehend
                    return review.criteriaScores.length === 0 && (!review?.reasoning || review.reasoning === '');
                }))
            } else {
                // Initiale Daten aus der API wieder setzen
                setFilteredData(data);
            }

            setShowNotReviewed(!showNotReviewed);
        };

        return (
            <input
                type="checkbox"
                checked={showNotReviewed}
                onChange={(e) => filterNotReviewedSubmissions()}
            />
        );
    };

    // siehe https://github.com/tannerlinsley/react-table/discussions/2664#discussioncomment-335443
    const columns = React.useMemo<Column<PaperSubmissionReviewListingData>[]>(
        () => [
            {
                id: 'eventName',
                Header: <FormattedMessage id="myarea.listing.column.header.event"/>,
                accessor: (row: PaperSubmissionReviewListingData) => row.event.name,
                Cell: (cell: Cell) => <span>{cell.row.values.eventName}</span>,
                Filter: ColumnFilter(intl.formatMessage({id: 'myarea.listing.column.header.event.filter.placeholder' }))
            },
            {
                id: 'title',
                Header: <FormattedMessage id="myarea.listing.column.header.title"/>,
                accessor: (row: PaperSubmissionReviewListingData) => row.submission.title,
                Cell: (cell: Cell) => <span>{cell.row.values.submission.title}</span>,
                Filter: ColumnFilter(intl.formatMessage({id: 'myarea.listing.column.header.title.filter.placeholder'}))
            },
            {
                id: 'topic',
                Header: <FormattedMessage id="myarea.listing.column.header.topic"/>,
                accessor: (row: PaperSubmissionReviewListingData) => row.submission?.topic[intl.locale],
                Cell: (cell: Cell) => <span>{cell.row.values.submission?.topic[intl.locale]}</span>,
                Filter: ColumnFilter(intl.formatMessage({id: 'myarea.listing.column.header.topic.filter.placeholder'}))
            },
            {
                id: 'review',
                Header: <FormattedMessage id="myarea.listing.column.header.review"/>,
                accessor: (row: PaperSubmissionReviewListingData) => {
                    return {criteriaScores: row.criteriaScores, reasoning: row.reasoning};
                },
                Cell: (cell: Cell) => ReviewsColumn(cell),
                disableFilters: true
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.reviewableUntil"/>,
                accessor: 'reviewableUntil' as const,
                Cell: (cell: Cell) => DaysUntilColumn({
                    submissionId: cell.row.values.submission.id,
                    date: cell.row.values.reviewableUntil
                }),
                disableFilters: true
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.actions"/>,
                accessor: 'submission' as const,
                Cell: (cell: Cell) => {
                    // da es keinen accessor für die ID gibt, muss dieser aus den Orginialdaten gefischt werden
                    // @ts-ignore FIXME Das Typing von ReactTable ist noch alg. wackelig, weshalb ich hier gerade
                    // nicht sagen kann, dass cell.row.original ein Feld "id" hat
                    const {id} = cell.row.original;

                    return ActionColumn({
                        callForPapersSlug: cell.row.values.submission.callForPapersUrlSlug,
                        submissionId: cell.row.values.submission.id,
                        reviewId: id
                    })
                },
                disableFilters: true
            }
        ],
        // TODO: Ist der KeycloakUser hier erforderich? Wenn ja, dann dokumentieren warum.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [keycloakUser]
    );

    // Tabellen-Props und State initialisieren
    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize}
    } = useTable(
        {
            columns,
            data: filteredData,
            defaultColumn: {Filter: <></>},
            initialState: {pageIndex: 0, pageSize: 10}
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    return (
        <div className="submission-listing-container">
            <h2>
                <FormattedMessage id="myarea.reviews.listing.title"/>
            </h2>

            <p>
                <FormattedMessage id="myarea.reviews.listing.subtitle"/>
            </p>

            {isLoading &&
                <PageLoadingIndicator/>
            }

            {!isEmpty(data) &&
                <>
                    <p>
                        <FormattedMessage id="myarea.reviews.listing.global_filter"/>
                        &nbsp;
                        <GlobalFilter/>
                    </p>

                    <BTable bordered hover size="sm" {...getTableProps()}>
                        <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th key={column.id}>
                                        <span {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? <i className="fa fa-sort-down"/>
                                                    : <i className="fa fa-sort-up"/>
                                                : <i className="fa fa-sort"/>
                                            }
                                        </span>

                                        {column.canFilter
                                            ? <span className="event-filter">
                                                <i className="fa fa-filter" onClick={() => setShowFilter(!showFilter)}/>
                                                {showFilter ? <div>{column.render('Filter')}</div> : null}
                                            </span>
                                            : null
                                        }
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody>
                        {page.map((row, i) => {
                            prepareRow(row)
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => {
                                        return (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                        </tbody>
                    </BTable>
                    <ReactTablePagination
                        page={page}
                        pageCount={pageCount}
                        pageOptions={pageOptions}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        gotoPage={gotoPage}
                        previousPage={previousPage}
                        nextPage={nextPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                    />
                </>
            }
        </div>
    );
};

export default PaperSubmissionReviewListing;
